import React, { forwardRef, ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActionMeta, createFilter, InputActionMeta, MenuPlacement } from 'react-select';
import { FixedSizeList as List } from 'react-window';
import { useTheme } from 'styled-components';

import { CreateSelectStyled, SelectStyled } from 'common/components/form/Select/Select.styled';
import { ISelectModel } from 'common/models';

import { InputOption } from './InputOption';
import { SingleValueTooltip, singleValueTooltipComponents } from './SingleValueTooltip';

const MenuList = (props: any) => {
  const { children, maxHeight = 300 } = props; // Set a default maxHeight
  const height = 30;

  return (
    <List height={maxHeight} itemCount={children.length} itemSize={height} width="100%">
      {({ index, style }: any) => <div style={style}>{children[index]}</div>}
    </List>
  );
};

export interface ISelect {
  name: string;
  options: ISelectModel<any>[];
  defaultValue?: any;
  disabled?: boolean;
  clearable?: boolean;
  isLoading?: boolean;
  hasError?: boolean;
  isMultiSelect?: boolean;
  isFilterDropdown?: boolean;
  onChange?: (option: any, additionalActions: ActionMeta<any>) => void;
  onBlur?: () => void;
  placeholder?: string;
  isMultiSelectDataLength?: string | number;
  overflowMenuPortalTarget?: boolean;
  onInputChange?: (value: string) => void;
  enableSearch?: boolean;
  menuIsOpen?: boolean;
  autoFocus?: boolean;
  disableDropdownArrow?: boolean;
  extendStyles?: {
    valueContainer?: React.CSSProperties;
    menuList?: React.CSSProperties;
    menu?: React.CSSProperties;
    menuPortal?: React.CSSProperties;
    control?: React.CSSProperties;
    placeholder?: React.CSSProperties;
    option?: (styles: any, additionalProps: any) => React.CSSProperties;
    singleValue?: React.CSSProperties;
  };
  disableMultiSelection?: boolean;
  dropdownMenuHeight?: string;
  showReactWindowList?: boolean;
  menuPlacement?: MenuPlacement;
  containerStyles?: string;
  onMenuOpen?: () => void;
  onCreateOption?: (inputValue: string) => void;
  createElementIfNotExistInDropdown?: boolean;
  elementNameIfNotExistInDropdown?: string;
  enableSingleValueTooltip?: boolean;
  keepSearchOnSelect?: boolean;
  formatOptionLabel?: (option: any, meta: any) => ReactNode;
  searchFromStart?: boolean;
}

export const Select = forwardRef(
  (
    {
      name,
      options,
      defaultValue,
      disabled,
      clearable,
      isLoading,
      hasError,
      isMultiSelect,
      isFilterDropdown,
      onChange,
      onBlur,
      placeholder,
      isMultiSelectDataLength,
      overflowMenuPortalTarget,
      onInputChange,
      enableSearch,
      menuIsOpen,
      autoFocus,
      disableDropdownArrow,
      extendStyles,
      disableMultiSelection,
      dropdownMenuHeight,
      showReactWindowList,
      menuPlacement,
      containerStyles,
      onMenuOpen,
      onCreateOption,
      createElementIfNotExistInDropdown,
      elementNameIfNotExistInDropdown,
      enableSingleValueTooltip,
      keepSearchOnSelect,
      formatOptionLabel,
      searchFromStart,
    }: ISelect,
    ref,
  ) => {
    const { t } = useTranslation();
    const { colors } = useTheme();
    const [searchValue, setSearchValue] = useState('');

    const handleInputChange = (newValue: string, actionMeta: InputActionMeta) => {
      if (keepSearchOnSelect) {
        if (actionMeta.action === 'set-value') return; // react-select clears the search on every pick

        setSearchValue(newValue);
      }

      onInputChange && onInputChange(newValue);
    };

    const customStyles = {
      option: (styles: any, additionalProps: any) => {
        const { isFocused } = additionalProps;

        if (extendStyles?.option) {
          return { ...styles, ...(extendStyles?.option || {}) };
        }

        return {
          ...styles,
          cursor: 'pointer',
          color: colors.black_100,
          '&:active': {
            backgroundColor: colors.black_10,
          },
          backgroundColor: isFocused ? colors.black_10 : null,
        };
      },
      control: (styles: any) => ({
        ...styles,
        ...(extendStyles?.control || {}),
      }),
      placeholder: (styles: any) => ({
        ...styles,
        ...(extendStyles?.placeholder || {}),
      }),
      valueContainer: (styles: any) => ({
        ...styles,
        ...(extendStyles?.valueContainer || {}),
      }),
      menuList: (styles: any) => ({
        ...styles,
        ...(extendStyles?.menuList || {}),
      }),
      menu: (styles: any) => ({
        ...styles,
        ...(extendStyles?.menu || {}),
      }),
      menuPortal: (styles: any) => ({
        ...styles,
        ...(extendStyles?.menuPortal || {}),
      }),
      singleValue: (styles: any) => ({
        ...styles,
        ...(extendStyles?.singleValue || {}),
      }),
    };

    const isMultiArray = !disableMultiSelection
      ? isMultiSelect && options.filter((item: ISelectModel<any>) => item.checked)
      : false;

    const SelectComponent = createElementIfNotExistInDropdown ? CreateSelectStyled : SelectStyled;

    return (
      <>
        <SelectComponent
          ref={ref as any}
          menuPortalTarget={overflowMenuPortalTarget ? document.body : null} // dropdown window to be on top of anything below
          $isMultiSelect={isMultiSelect && +isMultiSelectDataLength! > 0 ? true : false}
          isSearchable={options.length <= 10 && !enableSearch ? false : true}
          controlShouldRenderValue={!isMultiSelect}
          closeMenuOnSelect={!isMultiSelect}
          blurInputOnSelect={!isMultiSelect}
          hideSelectedOptions={false}
          styles={customStyles}
          $containerStyles={containerStyles}
          autoFocus={autoFocus}
          name={name}
          onCreateOption={onCreateOption}
          noOptionsMessage={({ inputValue }) => {
            if (!inputValue) return t('Start typing to search for');

            return t('No results for') + ` "${inputValue}"`;
          }}
          menuIsOpen={menuIsOpen}
          placeholder={
            placeholder
              ? isMultiSelect && Number(isMultiSelectDataLength) > 1
                ? `${isMultiSelectDataLength} ` + t(placeholder)
                : isMultiSelect && Number(isMultiSelectDataLength) === 1
                ? placeholder
                : t(placeholder)
              : t('Select...')
          }
          classNamePrefix="true"
          defaultValue={
            disableMultiSelection ? defaultValue : !isMultiSelect ? defaultValue : isMultiArray
          }
          formatCreateLabel={() => elementNameIfNotExistInDropdown}
          isClearable={clearable}
          isDisabled={disabled}
          isLoading={isLoading}
          $hasError={hasError}
          inputValue={keepSearchOnSelect ? searchValue : undefined}
          onInputChange={handleInputChange}
          onMenuOpen={onMenuOpen}
          isMulti={isMultiSelect}
          options={options}
          filterOption={
            searchFromStart
              ? createFilter({ matchFrom: 'start', stringify: option => option.label })
              : undefined
          }
          components={
            disableDropdownArrow
              ? {
                  DropdownIndicator: () => null,
                  IndicatorSeparator: () => null,
                  ...(isMultiSelect || !enableSingleValueTooltip
                    ? {}
                    : singleValueTooltipComponents),
                }
              : isMultiSelect
              ? {
                  Option: InputOption,
                  ...(showReactWindowList ? { MenuList } : {}),
                }
              : enableSingleValueTooltip
              ? singleValueTooltipComponents
              : {}
          }
          $isFilterDropdown={isFilterDropdown}
          formatOptionLabel={formatOptionLabel}
          onChange={onChange}
          onBlur={onBlur}
          $dropdownMenuHeight={dropdownMenuHeight}
          menuPlacement={menuPlacement}
        />
        {!isMultiSelect && enableSingleValueTooltip && <SingleValueTooltip name={name} />}
      </>
    );
  },
);

Select.displayName = 'Select';
